import React, { useEffect, useState, useCallback, useRef } from 'react';

const TASK_TYPES = [
  { key: 'CHECKOUT_CLEAN', label: 'Checkout Clean' },
  { key: 'STAYOVER', label: 'Stayover' },
  { key: 'VACANT_CLEAN', label: 'Vacant Clean' },
];

const INPUT_TYPES = [
  { key: 'YES_NO', label: 'Yes / No' },
  { key: 'PASS_FAIL', label: 'Pass / Fail' },
  { key: 'NUMERIC', label: 'Numeric' },
  { key: 'REMARKS', label: 'Remarks' },
];

const MAINT_CATEGORIES = ['ROOM', 'COMMON_AREA', 'EQUIPMENT', 'ELECTRICAL', 'PLUMBING', 'HVAC', 'FURNITURE', 'OTHER'];

/**
 * Housekeeping Checklist Master — admin-configurable checklist per task_type
 * (Checkout Clean / Stayover / Vacant Clean), filled by housekeeping staff on
 * the mobile app before a task is marked Complete. Mirrors HkSlaConfig's
 * pattern: load the current template, edit a local array, POST the whole
 * array back — the server reconciles (soft-deletes items removed from the
 * payload rather than hard-deleting, so past tasks' saved answers survive).
 */
function HkChecklistMaster({ token, selectedProperty }) {
  const [taskType, setTaskType] = useState('CHECKOUT_CLEAN');
  const [name, setName] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const nextLocalId = useRef(-1);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const load = useCallback(async (type) => {
    if (!token || !selectedProperty?.id) return;
    setLoading(true); setError(''); setSuccess('');
    try {
      const res = await fetch(`/api/v1/pms/housekeeping/checklist-master?task_type=${type}`, { headers: headers() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Failed to load checklist'); return; }
      setName(data.checklist?.name || `${TASK_TYPES.find(t => t.key === type)?.label} Checklist`);
      setItems((data.items || []).map(it => ({
        item_id: it.item_id,
        section: it.section || '',
        label: it.label,
        input_type: it.input_type,
        is_mandatory: it.is_mandatory,
        photo_required: it.photo_required,
        triggers_maintenance: it.triggers_maintenance,
        maintenance_category: it.maintenance_category || 'OTHER',
      })));
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally { setLoading(false); }
  }, [token, selectedProperty, headers]);

  useEffect(() => { load(taskType); }, [taskType, load]);

  const handleTaskTypeChange = (type) => setTaskType(type);

  const updateItem = (idx, patch) => {
    setItems(prev => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    setSuccess('');
  };

  const addItem = () => {
    setItems(prev => ([...prev, {
      item_id: nextLocalId.current--, section: '', label: '', input_type: 'YES_NO',
      is_mandatory: true, photo_required: false, triggers_maintenance: false, maintenance_category: 'OTHER',
    }]));
  };

  const removeItem = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setError(''); setSuccess(''); setSaving(true);
    try {
      const res = await fetch('/api/v1/pms/housekeeping/checklist-master', {
        method: 'POST',
        headers: { ...headers(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          task_type: taskType,
          name,
          items: items.map(it => ({
            item_id: it.item_id > 0 ? it.item_id : undefined,
            section: it.section,
            label: it.label,
            input_type: it.input_type,
            is_mandatory: it.is_mandatory,
            photo_required: it.photo_required,
            triggers_maintenance: it.triggers_maintenance,
            maintenance_category: it.maintenance_category,
          })),
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { setError(data.error || 'Failed to save'); return; }
      setSuccess('Checklist saved.');
      load(taskType);
    } catch (err) {
      setError(err?.message || 'Network error');
    } finally { setSaving(false); }
  };

  if (!selectedProperty?.id) {
    return <div style={{ padding: 24, color: '#6b7280' }}>Select a property to configure housekeeping checklists.</div>;
  }

  return (
    <div style={{ padding: 24, maxWidth: 960, margin: '0 auto' }}>
      <h2 style={{ marginBottom: 4 }}>Housekeeping Checklist Master</h2>
      <p style={{ color: '#666', fontSize: 14, marginBottom: 16 }}>
        Configure the checklist a housekeeper fills on their device before marking a room's cleaning task Complete.
        Items flagged <b>Triggers Maintenance</b> auto-raise a Maintenance work order if a supervisor's inspection
        finds them failed (No / Fail) and rejects the cleaning.
      </p>

      <div style={{ display: 'flex', gap: '0.45rem', flexWrap: 'wrap', marginBottom: 16 }}>
        {TASK_TYPES.map(t => (
          <button key={t.key} onClick={() => handleTaskTypeChange(t.key)}
            style={{
              padding: '0.42rem 0.9rem', borderRadius: 6, border: '1px solid #cbd5e1', cursor: 'pointer',
              background: taskType === t.key ? '#1976d2' : '#fff',
              color: taskType === t.key ? '#fff' : '#1e293b', fontWeight: 700,
            }}>
            {t.label}
          </button>
        ))}
      </div>

      {error && <div style={{ background: '#fde8e8', color: '#b71c1c', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 14 }}>{error}</div>}
      {success && <div style={{ background: '#e8f5e9', color: '#2e7d32', padding: '8px 12px', borderRadius: 4, marginBottom: 12, fontSize: 14 }}>{success}</div>}

      {loading ? <p>Loading…</p> : (
        <>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 4 }}>Checklist Name</label>
            <input value={name} onChange={e => setName(e.target.value)} style={{ ...inputStyle, maxWidth: 360 }} />
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <thead>
                <tr style={{ background: '#f5f5f5' }}>
                  <th style={thStyle}>Section</th>
                  <th style={thStyle}>Item Label</th>
                  <th style={thStyle}>Input Type</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Mandatory</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Photo Req.</th>
                  <th style={{ ...thStyle, textAlign: 'center' }}>Triggers Maintenance</th>
                  <th style={thStyle}>Maintenance Category</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it, idx) => (
                  <tr key={it.item_id}>
                    <td style={tdStyle}>
                      <input value={it.section} onChange={e => updateItem(idx, { section: e.target.value })}
                        placeholder="e.g. Bathroom" style={inputStyle} />
                    </td>
                    <td style={tdStyle}>
                      <input value={it.label} onChange={e => updateItem(idx, { label: e.target.value })}
                        placeholder="e.g. Towels replaced" style={inputStyle} />
                    </td>
                    <td style={tdStyle}>
                      <select value={it.input_type} onChange={e => updateItem(idx, { input_type: e.target.value })} style={inputStyle}>
                        {INPUT_TYPES.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                      </select>
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input type="checkbox" checked={it.is_mandatory} onChange={e => updateItem(idx, { is_mandatory: e.target.checked })} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input type="checkbox" checked={it.photo_required} onChange={e => updateItem(idx, { photo_required: e.target.checked })} />
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <input type="checkbox" checked={it.triggers_maintenance} onChange={e => updateItem(idx, { triggers_maintenance: e.target.checked })} />
                    </td>
                    <td style={tdStyle}>
                      {it.triggers_maintenance ? (
                        <select value={it.maintenance_category} onChange={e => updateItem(idx, { maintenance_category: e.target.value })} style={inputStyle}>
                          {MAINT_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                    </td>
                    <td style={tdStyle}>
                      <button onClick={() => removeItem(idx)} title="Remove item"
                        style={{ border: 'none', background: 'none', color: '#b71c1c', cursor: 'pointer', fontWeight: 700 }}>×</button>
                    </td>
                  </tr>
                ))}
                {!items.length && (
                  <tr><td colSpan={8} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No checklist items yet. Add one below.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 12, display: 'flex', gap: 10 }}>
            <button onClick={addItem}
              style={{ padding: '7px 16px', background: '#fff', color: '#1976d2', border: '1px solid #1976d2', borderRadius: 4, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
              + Add Item
            </button>
            <button onClick={handleSave} disabled={saving}
              style={{ padding: '8px 24px', background: saving ? '#999' : '#1976d2', color: '#fff', border: 'none', borderRadius: 4, cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600 }}>
              {saving ? 'Saving…' : 'Save Checklist'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

const thStyle = { padding: '10px 8px', textAlign: 'left', borderBottom: '2px solid #ddd', whiteSpace: 'nowrap' };
const tdStyle = { padding: '6px 8px', borderBottom: '1px solid #e0e0e0' };
const inputStyle = { width: '100%', padding: '6px 8px', border: '1px solid #ccc', borderRadius: 4, fontSize: 13.5, boxSizing: 'border-box' };

export default HkChecklistMaster;
